import React, { useEffect, useState } from "react";
import { CircularProgress, Typography, Button, Paper, Box, Stack } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Print as PrintIcon,
  Home as HomeIcon,
  MonitorHeart as DashboardIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { checkPaymentStatus } from "../actions/payment.actions";
import { useSelector, useDispatch } from "react-redux";
import { applyUserData } from "../../../utils/src/auth";
import { getCheckoutToken } from "../utils/paymentSession";

// Styled components
const RootContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  padding: theme.spacing(4, 2, 6),
  background: `radial-gradient(circle at top, ${alpha(
    theme.palette.primary.main,
    0.08,
  )} 0, transparent 34%), linear-gradient(180deg, ${alpha(
    theme.palette.grey[50],
    0.92,
  )} 0%, ${theme.palette.background.default} 100%)`,
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  maxWidth: 860,
  width: "100%",
  margin: "0 auto",
  overflow: "hidden",
  borderRadius: 24,
  border: `1px solid ${alpha(theme.palette.divider, 0.65)}`,
  boxShadow: "0 24px 80px rgba(15, 23, 42, 0.14)",
}));

const CenterContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(5, 4, 5),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(4, 2.5, 4),
  },
}));

const StatusText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const SuccessIcon = styled(CheckCircleIcon)(({ theme }) => ({
  color: theme.palette.success.main,
}));

const ErrorIconStyled = styled(ErrorIcon)(({ theme }) => ({
  color: theme.palette.error.main,
}));

const WarningIconStyled = styled(ErrorIcon)(({ theme }) => ({
  color: theme.palette.warning.main,
}));

const DetailsBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  width: "100%",
  textAlign: "center",
  maxWidth: 720,
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(160px, 1fr) minmax(0, 1.8fr)",
  gap: theme.spacing(1.5, 2),
  alignItems: "start",
  padding: theme.spacing(1.25, 0),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
  "&:last-of-type": {
    borderBottom: "none",
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
    gap: theme.spacing(0.5),
    textAlign: "left",
  },
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.secondary,
}));

const ActionBar = styled(Stack)(({ theme }) => ({
  marginTop: theme.spacing(4),
  width: "100%",
  justifyContent: "center",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(1.5),
  [theme.breakpoints.up("sm")]: {
    flexDirection: "row",
    alignItems: "center",
  },
}));

/**
 * Completed payment component
 *
 * Expected API response structure from checkPaymentStatus():
 * {
 *   data: {
 *     status: boolean,
 *     data: {
 *       status: "succeeded" | "failed" | "pending",
 *       amount_display: string,
 *       plan: string,
 *       transaction_id: string,
 *       transaction_dt: string,
 *       user: {
 *         first_name: string,
 *         last_name: string,
 *         email: string,
 *         phone_number: string
 *       }
 *     }
 *   }
 * }
 *
 * @returns {JSX.Element}
 * @component
 */
const PaymentComplete = () => {
  const auth = useSelector((state) => state.auth) || {};
  const user = useSelector((state) => state.user?.data) || {};
  const isAuthenticated = Boolean(auth?.isAuthenticated);
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const [paymentStatus, setPaymentStatus] = useState("processing");
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState(null);
  const [maxRetries] = useState(10); // Try up to 10 times
  const [retryCount, setRetryCount] = useState(0);

  const searchParams = new URLSearchParams(window.location.search);
  const payment_intent = searchParams.get("payment_intent");
  const payment_intent_client_secret = searchParams.get(
    "payment_intent_client_secret",
  );
  const payment_type = searchParams.get("payment_type"); // could be "online_application" or "register_member"
  const registration_token = searchParams.get("registration_token");
  const application_id = searchParams.get("application_id");
  const checkout_token = getCheckoutToken({
    applicationId: application_id,
    checkoutToken: searchParams.get("checkout_token"),
  });
  const free_plan = searchParams.get("free_plan") === "true";

  useEffect(() => {
    if (free_plan) {
      if (application_id) {
        checkPayment();
      } else {
        setPaymentStatus("success");
        setPaymentDetails({
          amount_display: "$0.00",
          plan: "Free Membership",
          transaction_id: payment_intent || "N/A (Free)",
          transaction_dt: new Date().toLocaleDateString(),
          free_plan: true,
        });
      }
      return;
    }

    // Initial check for paid plans
    checkPayment();

    // Keep polling for status while processing, with exponential backoff
    const intervalId = setInterval(
      () => {
        if (paymentStatus === "processing" && retryCount < maxRetries) {
          checkPayment();
          setRetryCount(retryCount + 1);
        } else if (retryCount >= maxRetries && paymentStatus === "processing") {
          // Max retries reached but payment still processing
          setPaymentStatus("unknown");
          clearInterval(intervalId);
        }
      },
      2000 * Math.pow(1.5, retryCount),
    ); // Exponential backoff

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatus, retryCount, free_plan]);

  const checkPayment = async () => {
    // For free plans, we only check for user information if we have application_id
    if (free_plan && !application_id) {
      return;
    }

    // For paid plans, we need payment_intent
    if (!free_plan && !payment_intent) {
      setError("Payment information is missing. Cannot verify payment status.");
      setPaymentStatus("error"); // Set status to error if essential info is missing
      return;
    }

    // Prepare data payload for status check
    const payload = {
      payment_type,
    };

    // Add payment_intent for paid plans
    if (!free_plan && payment_intent) {
      payload.payment_intent = payment_intent;
      payload.payment_intent_client_secret = payment_intent_client_secret;
    }

    // Context-specific identifiers
    if (registration_token) {
      payload.registration_token = registration_token;
    }
    if (application_id) {
      payload.application_id = parseInt(application_id, 10);
    }
    if (checkout_token) {
      payload.checkout_token = checkout_token;
    }

    // Check payment status with server
    checkPaymentStatus(payload)
      .then(async (response) => {
        // Axios response object: { data: { status: boolean, data: object, message: string }, status: httpStatusCode, ... }

        // Check the application-level status from ApiBaseController
        if (response.data.status) {
          const appData = response.data.data;
          log.info("appData", appData);

          const normalizedDetails = free_plan
            ? {
                amount_display: appData.amount_display || "$0.00",
                plan: appData.plan || "Free Membership",
                transaction_id:
                  appData.transaction_id || payment_intent || "N/A (Free)",
                transaction_dt:
                  appData.transaction_dt || new Date().toLocaleDateString(),
                free_plan: true,
                ...appData,
              }
            : appData;

          setPaymentDetails(normalizedDetails);

          if (appData.status === "succeeded") {
            setPaymentStatus("success");
            const userData = auth?.user || user || null;
            try {
              const _data = await applyUserData({
                dispatch,
                userData,
              });
              log.debug("Payment success - refreshed user data: ", _data);
            } catch (err) {
              log.error("Error refreshing user data after payment", err);
            }
          } else if (appData.status === "failed") {
            setPaymentStatus("failed");
          } else if (paymentStatus !== "processing") {
            setPaymentStatus("processing");
          }
        } else {
          // Handle application-level error reported by the backend (response.data.status === false)
          throw new Error(
            response.data.message || "Error checking payment status",
          );
        }
      })
      .catch((err) => {
        log.error("Payment status check error:", err);
        // Use error message from backend if available (via ApiBaseController::sendError)
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "An error occurred while checking payment status. Please contact support.";
        setError(errorMessage);
        setPaymentStatus("error");
      });
  };

  const handlePrint = () => window.print(); // Trigger browser's print dialog

  const statusTitle =
    paymentStatus === "success"
      ? "Payment Successful!"
      : paymentStatus === "failed"
        ? "Payment Failed"
        : paymentStatus === "unknown"
          ? "Payment Status Unknown"
          : paymentStatus === "error"
            ? "Error Checking Payment"
            : "Processing your payment...";

  const statusDescription =
    paymentStatus === "success"
      ? "Your payment was verified and your membership details are shown below."
      : paymentStatus === "failed"
        ? "The payment could not be completed. Please try again or contact support."
        : paymentStatus === "unknown"
          ? "The payment may still be processing. Check back shortly or contact support if this persists."
          : paymentStatus === "error"
            ? error || "An error occurred while checking payment status."
            : "Please wait while we verify your transaction.";

  const statusTone =
    paymentStatus === "success"
      ? "success"
      : paymentStatus === "failed"
        ? "error"
        : paymentStatus === "unknown"
          ? "warning"
          : paymentStatus === "error"
            ? "error"
            : "info";

  return (
    <RootContainer>
      <StyledPaper>
        <CenterContent>
          <Box sx={{ mb: 1.5, display: "grid", placeItems: "center" }}>
            {paymentStatus === "processing" ? (
              <CircularProgress size={42} thickness={4} color="inherit" />
            ) : paymentStatus === "success" ? (
              <SuccessIcon style={{ fontSize: 72 }} />
            ) : paymentStatus === "failed" || paymentStatus === "error" ? (
              <ErrorIconStyled style={{ fontSize: 72 }} />
            ) : (
              <WarningIconStyled style={{ fontSize: 72 }} />
            )}
          </Box>

          <StatusText
            variant="h5"
            sx={{ mt: 0, fontWeight: 800, textAlign: "center" }}
          >
            {statusTitle}
          </StatusText>
          <Typography
            variant="body1"
            color={statusTone === "error" ? "error" : "text.secondary"}
            sx={{ maxWidth: 660, mt: 1, textAlign: "center" }}
          >
            {statusDescription}
          </Typography>

          {paymentStatus === "success" && (
            <>
              {(paymentDetails?.user?.first_name ||
                user.first_name ||
                paymentDetails?.user?.last_name ||
                user.last_name ||
                paymentDetails?.user?.email ||
                user.email_id ||
                paymentDetails?.user?.phone_number ||
                user.phone_number) && (
                <DetailsBox sx={{ mt: 2, mb: 2 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 800 }}
                  >
                    Customer Information
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {(paymentDetails?.user?.first_name || user.first_name) && (
                      <DetailRow>
                        <DetailLabel variant="body2">First Name</DetailLabel>
                        <Typography variant="body1">
                          {paymentDetails?.user?.first_name || user.first_name}
                        </Typography>
                      </DetailRow>
                    )}
                    {(paymentDetails?.user?.last_name || user.last_name) && (
                      <DetailRow>
                        <DetailLabel variant="body2">Last Name</DetailLabel>
                        <Typography variant="body1">
                          {paymentDetails?.user?.last_name || user.last_name}
                        </Typography>
                      </DetailRow>
                    )}
                    {(paymentDetails?.user?.email || user.email_id) && (
                      <DetailRow>
                        <DetailLabel variant="body2">Email</DetailLabel>
                        <Typography variant="body1">
                          {paymentDetails?.user?.email || user.email_id}
                        </Typography>
                      </DetailRow>
                    )}
                    {(paymentDetails?.user?.phone_number ||
                      user.phone_number) && (
                      <DetailRow>
                        <DetailLabel variant="body2">Phone</DetailLabel>
                        <Typography variant="body1">
                          {paymentDetails?.user?.phone_number ||
                            user.phone_number}
                        </Typography>
                      </DetailRow>
                    )}
                  </Box>
                </DetailsBox>
              )}
              {paymentDetails && (
                <DetailsBox sx={{ mt: 2, mb: 2 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 800 }}
                  >
                    Payment Details
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <DetailRow>
                      <DetailLabel variant="body2">Amount</DetailLabel>
                      <Typography variant="body1">
                        {paymentDetails.amount_display}
                      </Typography>
                    </DetailRow>
                    {paymentDetails.plan && (
                      <DetailRow>
                        <DetailLabel variant="body2">Product</DetailLabel>
                        <Typography variant="body1">
                          SMFA - {paymentDetails.plan}
                        </Typography>
                      </DetailRow>
                    )}
                    {paymentDetails.transaction_id && (
                      <DetailRow>
                        <DetailLabel variant="body2">
                          Transaction ID
                        </DetailLabel>
                        <Typography
                          variant="body1"
                          sx={{ wordBreak: "break-all" }}
                        >
                          {paymentDetails.transaction_id}
                        </Typography>
                      </DetailRow>
                    )}
                    {paymentDetails.transaction_dt && (
                      <DetailRow>
                        <DetailLabel variant="body2">Date</DetailLabel>
                        <Typography variant="body1">
                          {paymentDetails.transaction_dt}
                        </Typography>
                      </DetailRow>
                    )}
                    {paymentDetails.term?.term_label && (
                      <DetailRow>
                        <DetailLabel variant="body2">
                          Membership Term
                        </DetailLabel>
                        <Typography variant="body1">
                          {paymentDetails.term.term_label}
                        </Typography>
                      </DetailRow>
                    )}
                    {paymentDetails.display?.coverage_label && (
                      <DetailRow>
                        <DetailLabel variant="body2">Coverage</DetailLabel>
                        <Typography variant="body1">
                          {paymentDetails.display.coverage_label}
                        </Typography>
                      </DetailRow>
                    )}
                  </Box>
                </DetailsBox>
              )}
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ maxWidth: 660, mt: 1, textAlign: "center" }}
              >
                Thank you for your payment.
                {payment_type === "online_application" &&
                  !isAuthenticated &&
                  free_plan && (
                    <>
                      <br />
                      <strong>
                        If this is your first time signing up, we need to
                        manually approve your complimentary application. Please
                        wait up to 2 business days for this to complete.
                      </strong>
                    </>
                  )}
              </Typography>
            </>
          )}

          <ActionBar>
            {isAuthenticated && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/dashboard")}
                startIcon={<DashboardIcon />}
                disabled={paymentStatus === "processing"}
                size="large"
                sx={{
                  minWidth: { xs: "100%", sm: 170 },
                  height: 52,
                  borderRadius: 999,
                  px: 3,
                  py: 1.25,
                }}
              >
                Dashboard
              </Button>
            )}
            <Button
              variant={isAuthenticated ? "outlined" : "contained"}
              color="primary"
              onClick={() => navigate("/")}
              startIcon={<HomeIcon />}
              disabled={paymentStatus === "processing"}
              size="large"
              sx={{
                minWidth: { xs: "100%", sm: 170 },
                height: 52,
                borderRadius: 999,
                px: 3,
                py: 1.25,
              }}
            >
              Home
            </Button>
            {/* Add Print Button - Conditionally show for success/relevant statuses */}
            {(paymentStatus === "success" ||
              paymentStatus === "failed" ||
              paymentStatus === "unknown") && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handlePrint}
                startIcon={<PrintIcon />}
                disabled={paymentStatus === "processing"}
                size="large"
                sx={{
                  minWidth: { xs: "100%", sm: 190 },
                  height: 52,
                  borderRadius: 999,
                  px: 3,
                  py: 1.25,
                }}
              >
                Print Receipt
              </Button>
            )}
          </ActionBar>
        </CenterContent>
      </StyledPaper>
    </RootContainer>
  );
};

export default PaymentComplete;
